import { useState } from "react";
import PageWrapper from "../components/PageWrapper";
import { motion } from "framer-motion";
import {
  FaEnvelope,
  FaPhone,
  FaClock,
  FaLocationDot,
  FaArrowRight,
  FaCheck,
} from "react-icons/fa6";
import "./Contact.css";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle, travelAgencySchema } from "../lib/seo";
import { useBrand, telHref, whatsappHref, addressLine } from "../brand/BrandContext";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

function Contact() {
  const brand = useBrand();
  usePageMeta({
    title: pageTitle("Contact Us"),
    description: `Contact ${brand.name}, ${addressLine(brand.address)}. Call ${brand.phone} or email ${brand.email} — ${brand.hours}.`,
    jsonLd: travelAgencySchema(),
  });
  const [status, setStatus] = useState("idle"); // idle | success

  return (
    <PageWrapper>
      <div className="contact">
        {/* HERO */}
        <section className="contact-hero">
          <div className="contact-hero-bg" aria-hidden />
          <div className="container contact-hero-inner">
            <motion.div initial="hidden" animate="show" variants={stagger}>
              <motion.h1 className="contact-title" variants={fadeUp}>
                Contact <span className="contact-title-accent">Us</span>
              </motion.h1>
              <motion.p className="contact-sub" variants={fadeUp}>
                Planning a trip? Need assistance with packages, visas, hotels, or
                cab services? We're here to help you every step of the way.
              </motion.p>

              <motion.div className="contact-pill-row" variants={fadeUp}>
                <span className="pill2">
                  <FaCheck /> Professional support
                </span>
                <span className="pill2">
                  <FaCheck /> Fast response
                </span>
                <span className="pill2">
                  <FaCheck /> Secure booking flow
                </span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* MAIN */}
        <section className="section">
          <div className="container">
            <motion.div
              className="contact-grid"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={stagger}
            >
              {/* LEFT: INFO */}
              <motion.div className="panel" variants={fadeUp}>
                <div className="panel-head">
                  <h2 className="panel-title">Get in Touch</h2>
                  <p className="panel-sub">
                    At <strong>{brand.name}</strong>, we believe
                    travel should be stress-free. Reach out to us for
                    personalized travel solutions, exclusive deals, and expert
                    guidance.
                  </p>
                </div>

                <div className="info-cards">
                  <div className="info-card">
                    <div className="info-ic">
                      <FaEnvelope />
                    </div>
                    <div>
                      <div className="info-k">Email</div>
                      <div className="info-v"><a href={`mailto:${brand.email}`}>{brand.email}</a></div>
                    </div>
                  </div>

                  <div className="info-card">
                    <div className="info-ic">
                      <FaLocationDot />
                    </div>
                    <div>
                      <div className="info-k">Office Address</div>
                      <div className="info-v">{addressLine(brand.address)}</div>
                    </div>
                  </div>

                  <div className="info-card">
                    <div className="info-ic">
                      <FaPhone />
                    </div>
                    <div>
                      <div className="info-k">Phone</div>
                      <div className="info-v"><a href={telHref(brand.phone)}>{brand.phone}</a></div>
                    </div>
                  </div>

                  <div className="info-card">
                    <div className="info-ic">
                      <FaClock />
                    </div>
                    <div>
                      <div className="info-k">Working Hours</div>
                      <div className="info-v">{brand.hours}</div>
                    </div>
                  </div>
                </div>

                <div className="contact-actions">
                  <a
                    href={whatsappHref(brand.whatsapp, "Hello I am interested in booking a travel package")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Chat on WhatsApp <FaArrowRight />
                  </a>
                  <a
                    href={brand.mapDirectionsUrl || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressLine(brand.address))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost"
                  >
                    Get Directions <FaLocationDot />
                  </a>
                </div>

                {brand.mapEmbedUrl && (
                  <div className="map-wrap">
                    <iframe
                      title={`${brand.name} location`}
                      src={brand.mapEmbedUrl}
                      width="100%"
                      height="280"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                )}
              </motion.div>

              {/* RIGHT: FORM */}
              <motion.div className="panel panel-form" variants={fadeUp}>
                <div className="panel-head">
                  <h2 className="panel-title">Send Us a Message</h2>
                  <p className="panel-sub">
                    Share your travel plan and we'll respond with options and a
                    clean booking flow.
                  </p>
                </div>

                {status === "success" && (
                  <div className="success">
                    <strong>Enquiry received.</strong> We'll get back to you
                    shortly.
                  </div>
                )}

                <form
                  className="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setStatus("success");
                  }}
                >
                  <div className="field">
                    <label className="label">Your Full Name</label>
                    <input className="input" type="text" placeholder="Your Full Name" required />
                  </div>

                  <div className="field">
                    <label className="label">Your Email Address</label>
                    <input className="input" type="email" placeholder="Your Email Address" required />
                  </div>

                  <div className="field">
                    <label className="label">Phone Number</label>
                    <input className="input" type="text" placeholder="Phone Number" />
                  </div>

                  <div className="field full">
                    <label className="label">Tell us about your travel plans</label>
                    <textarea
                      className="input"
                      rows={6}
                      placeholder="Tell us about your travel plans"
                      required
                    />
                  </div>

                  <div className="form-actions full">
                    <motion.button
                      className="btn btn-primary"
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                    >
                      Submit Enquiry <FaArrowRight />
                    </motion.button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setStatus("idle")}
                    >
                      Reset
                    </button>
                  </div>

                  <p className="fineprint full">
                    We never share your contact details. This form is for enquiry
                    & booking assistance only.
                  </p>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* EXTRA INFO */}
        <section className="section contact-extra">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={stagger}
            >
              <motion.h2 className="extra-title" variants={fadeUp}>
                Why Contact Us?
              </motion.h2>
              <motion.ul className="extra-list" variants={stagger}>
                <motion.li variants={fadeUp}>✔ Customized travel packages</motion.li>
                <motion.li variants={fadeUp}>✔ Trusted visa & documentation support</motion.li>
                <motion.li variants={fadeUp}>✔ Reliable cab & hotel partnerships</motion.li>
                <motion.li variants={fadeUp}>✔ Transparent pricing with no hidden charges</motion.li>
              </motion.ul>
            </motion.div>
          </div>
        </section>
      </div>
    </PageWrapper>
  );
}

export default Contact;